<template>
  <div style="height: 100%">
    <FullHeightRadialLoader :isLoading="isLoading" :loader-msg="$t('components:common.loading')"/>
    <FullHeightRadialLoader :isLoading="isSaving" :loader-msg="$t('components:loading-dialog.is-saving')"/>
    <v-card v-show="!isLoading && !isSaving" class="pa-5 pa-sm-10 onboarding-bg" elevation="0">
      <v-card-text class="pa-0">
        <v-form ref="form">
          <v-row justify="center" class="mr-0 ml-0 mb-3">
            <v-col cols="12" class="pa-0">
              <TaskCard
                  :taskName="$t('book/public:qrcode.common.onboarding')"
                  :additionalNames="getAdditionalNames(building, facility)"
                  taskStatus="job-accepted-valid"
                  hide-user
                  additional-name2-as-btn
                  @clickedAdditional2="showFacility"
              />
            </v-col>
          </v-row>
          <v-row justify="space-between" class="mr-0 ml-0 info-panel pa-sm-10">
            <v-col cols="12">
              <h4>{{ $t('book/public:qrcode.onboarding.information-title') }}</h4>
            </v-col>
            <v-col cols="12" sm="6" md="5">
              <LabelField
                  :label="$t('common:facility-number')"
                  :value="facility.objectNumber"
              />

              <LabelField
                  :label="$t('book/public:qrcode.onboarding.labels.facility-location')"
                  :value="facility.buildingStreet + ', ' + facility.buildingZip + ' ' + facility.buildingCity"
              />

              <LabelField
                  :label="$t('book/public:qrcode.onboarding.labels.facility-type')"
                  :value="facility.typeName"
              />
            </v-col>
            <v-col cols="12" sm="6" md="5">
              <LabelField
                  :label="$t('book/public:qrcode.onboarding.labels.recording-date')"
                  :value="currentDate"
              />

              <LabelField
                  :label="$t('book/public:qrcode.onboarding.labels.recording-person')"
                  :value="user.name + ' - ' + customer.name"
              />
            </v-col>
            <v-col cols="12">
              <v-alert
                  v-if="technicalInfo.reducedShaftHead || technicalInfo.reducedShaftPit"
                  outlined
                  type="warning"
                  prominent
                  border="left"
                  color="orange"
              >
                {{ getShaftMineWarningText }}
              </v-alert>
            </v-col>
          </v-row>

          <h4 class="font-weight-bold primary--text mt-5 mb-2">{{ $t('book/public:qrcode.onboarding.info-title') }}</h4>
          <v-expansion-panels tile flat v-model="technicalPanels">
            <!-- Technical Panel -->
            <v-expansion-panel ref="panel_0" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="primary--text mb-0">{{ $t('book/public:qrcode.onboarding.technical-info-title') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(technicalPanels === 0) ? technicalPanels = null : technicalPanels = 0"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.insert-data') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-select
                        v-model="technicalInfo.drive"
                        outlined
                        :label="$t('book/technical-info:labels.drive')"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :items="restDrives"
                        clearable
                        @change="getSubDrive(technicalInfo.drive)"
                        item-text="translatedName"
                        item-value="name"
                    />

                    <v-select
                        v-if="technicalInfo.drive === 'HYDRAULIC'"
                        v-model="technicalInfo.hydraulicDriveType"
                        outlined
                        :label="$t('book/technical-info:labels.hydraulic-drive')"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :items="restHydraulicDriveType"
                        clearable
                        item-value="name"
                        item-text="translatedName"
                    />

                    <v-select
                        v-else-if="technicalInfo.drive === 'ROPE'"
                        v-model="technicalInfo.ropeDriveTypes"
                        outlined
                        :label="$t('book/technical-info:labels.rope-drive')"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :items="restRopeDriveType"
                        clearable
                        multiple
                        chips
                        deletable-chips
                        item-value="name"
                        item-text="translatedName"
                    />

                    <v-select
                        v-model="technicalInfo.doorPosition"
                        outlined
                        :label="$t('book/technical-info:labels.door-position')"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :items="restDoorPositions"
                        clearable
                        item-value="name"
                        item-text="translatedName"
                    />

                    <v-text-field
                        v-model="technicalInfo.maxLoad"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.max-load')"
                        :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                        type="number"
                    />

                    <v-text-field
                        v-model="technicalInfo.maxPersons"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.max-persons')"
                        :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                        type="number"
                    />

                    <v-text-field
                        v-model="technicalInfo.operatingSpeed"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.operating-speed')"
                        :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                        type="number"
                    />
                  </v-col>

                  <v-col cols="12" md="5">
                    <v-text-field
                        v-model="technicalInfo.cabinHeight"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.cabin-height')"
                        :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                        type="number"
                    />

                    <v-text-field
                        v-model="technicalInfo.control"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.controls')"
                    />

                    <v-text-field
                        v-model="technicalInfo.frequencyConverter"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.frequency-converter')"
                    />

                    <v-select
                        v-model="technicalInfo.doorSecurityType"
                        outlined
                        :label="$t('book/technical-info:labels.door-security')"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :items="restDoorSecurityType"
                        clearable
                        item-value="name"
                        item-text="translatedName"
                    >
                    </v-select>

                    <CustomerTypeahead
                        ref="manufacturerTypeahead"
                        :value="technicalInfo.manufacturer"
                        @input="technicalInfo.manufacturer = $event"
                        :label="$t('book/technical-info:labels.manufacturer')"
                    />

                    <v-text-field
                        v-model="technicalInfo.unknownManufacturer"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.other-manufacturer')"
                    />
                  </v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-checkbox
                        v-model="technicalInfo.knownOperator"
                        :label="$t('book/technical-info:labels.known-operator')"
                        color="secondary"
                    />

                    <v-checkbox
                        v-model="technicalInfo.awmModule"
                        :label="$t('book/technical-info:labels.AWM-module')"
                        color="secondary"
                    />
                  </v-col>
                  <v-col cols="12" md="5"></v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <BaseButton
                        color="primary"
                        :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                        @clickedButton="technicalPanels++"
                    />
                  </v-col>
                  <v-col cols="12" md="5"></v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel ref="panel_1" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="primary--text mb-0">{{ $t('book/technical-info:titles.installation') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(technicalPanels === 1) ? technicalPanels = null : technicalPanels = 1"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.insert-data') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-text-field
                        v-model="technicalInfo.factoryNumber"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.factory-number')"
                    />

                    <v-text-field
                        v-model="technicalInfo.buildYear"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.build-year')"
                        :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                        type="number"
                    />

                    <DateField
                        ref="dateModernized"
                        @update="(v) => (technicalInfo.modernizedOn = v)"
                        :isClearable="true"
                        :label="$t('book/technical-info:labels.date-modernized')"
                    />

                    <DateField
                        ref="datePermit"
                        @update="(v) => (technicalInfo.buildingPermit = v)"
                        :isClearable="true"
                        :label="$t('book/technical-info:labels.building-permit')"
                    />
                  </v-col>

                  <v-col cols="12" md="5">
                    <DateField
                        ref="datePermission"
                        @update="(v) => (technicalInfo.permissionToUse = v)"
                        :isClearable="true"
                        :label="$t('book/technical-info:labels.permission-date')"
                    />

                    <CustomerTypeahead
                        ref="assemblerTypeahead"
                        :value="technicalInfo.assembler"
                        @input="technicalInfo.assembler = $event"
                        :label="$t('book/technical-info:labels.assembler')"
                    />

                    <v-text-field
                        v-model="technicalInfo.unknownAssembler"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.other-assembler')"
                    />
                  </v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <BaseButton
                        color="primary"
                        :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                        @clickedButton="technicalPanels++"
                    />
                  </v-col>
                  <v-col cols="12" md="5"></v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Type Panel -->
            <v-expansion-panel ref="panel_2" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="primary--text mb-0">{{ $t('common:type') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(technicalPanels === 2) ? technicalPanels = null : technicalPanels = 2"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.insert-data') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-select
                        v-model="technicalInfo.type"
                        outlined
                        :label="$t('book/technical-info:labels.elevator-type')"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :items="restElevatorType"
                        clearable
                        item-value="name"
                        item-text="translatedName"
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-checkbox
                        v-model="technicalInfo.firefighterLift"
                        :label="$t('book/technical-info:labels.firefighters-lift')"
                        color="secondary"
                    />
                  </v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-checkbox
                        v-model="technicalInfo.reducedShaftHead"
                        :label="$t('book/technical-info:labels.reduced-shaft-head')"
                        color="secondary"
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-checkbox
                        v-model="technicalInfo.reducedShaftPit"
                        :label="$t('book/technical-info:labels.reduced-shaft-pit')"
                        color="secondary"
                    />
                  </v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <BaseButton
                        color="primary"
                        :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                        @clickedButton="technicalPanels++"
                    />
                  </v-col>
                  <v-col cols="12" md="5"></v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Connected facilities -->
            <v-expansion-panel ref="panel_3" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="primary--text mb-0">{{ $t('book/technical-info:titles.connected-facilities') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(technicalPanels === 3) ? technicalPanels = null : technicalPanels = 3"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.insert-data') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                    <FacilityConnectionView
                        v-model="technicalInfo.connectedFacilities"
                        :technical-info="technicalInfo"
                        :selectableFacilities="restConnectableFacilities"
                    ></FacilityConnectionView>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Transport Panel -->
            <v-expansion-panel ref="panel_4" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="primary--text mb-0">{{ $t('book/technical-info:titles.transport') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(technicalPanels === 4) ? technicalPanels = null : technicalPanels = 4"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.insert-data') }}
                    </v-btn>
                  </v-col>
                </v-row>

              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-checkbox
                        v-model="technicalInfo.transportPerson"
                        :label="$t('book/technical-info:labels.transport-person')"
                        color="secondary"
                    />
                    <v-checkbox
                        v-model="technicalInfo.transportFreight"
                        :label="$t('book/technical-info:labels.transport-freight')"
                        color="secondary"
                    />
                    <v-checkbox
                        v-model="technicalInfo.transportGoods"
                        :label="$t('book/technical-info:labels.transport-goods')"
                        color="secondary"
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-checkbox
                        v-model="technicalInfo.transportCar"
                        :label="$t('book/technical-info:labels.transport-car')"
                        color="secondary"
                    />
                    <v-checkbox
                        v-model="technicalInfo.transportBed"
                        :label="$t('book/technical-info:labels.transport-bed')"
                        color="secondary"
                    />
                  </v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <BaseButton
                        color="primary"
                        :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                        @clickedButton="technicalPanels++"
                    />
                  </v-col>
                  <v-col cols="12" md="5"></v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Story Panel -->
            <v-expansion-panel ref="panel_5" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="primary--text mb-0">{{ $t('book/technical-info:titles.story') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(technicalPanels === 5) ? technicalPanels = null : technicalPanels = 5"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.insert-data') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-text-field
                        v-model="technicalInfo.laneStart"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.lane-start')"
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-text-field
                        v-model="technicalInfo.laneEnd"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.lane-end')"
                    />
                  </v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-text-field
                        v-model="technicalInfo.stations"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.stations')"
                        :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                        type="number"
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-text-field
                        v-model="technicalInfo.loadingStations"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.loading-stations')"
                        :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                        type="number"
                    />
                  </v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <BaseButton
                        color="primary"
                        :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                        @clickedButton="technicalPanels++"
                    />
                  </v-col>
                  <v-col cols="12" md="5"></v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Key Safe & Engine Room Panel -->
            <v-expansion-panel ref="panel_6" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="primary--text mb-0">
                      {{ $t('book/technical-info:titles.key-safe') }} &
                      {{ $t('book/technical-info:titles.engine') }}
                    </h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(technicalPanels === 6) ? technicalPanels = null : technicalPanels = 6"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.insert-data') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-checkbox
                        v-model="technicalInfo.keySafeExists"
                        :label="$t('book/technical-info:labels.key-safe-exists')"
                        color="secondary"
                    />

                    <v-textarea
                        v-show="technicalInfo.keySafeExists"
                        v-model="technicalInfo.keySafeInfo"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.key-safe-info')"
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-checkbox
                        v-model="technicalInfo.engineRoomExists"
                        :label="$t('book/technical-info:labels.engine-room-exists')"
                        color="secondary"
                    />

                    <v-select
                        v-if="technicalInfo.engineRoomExists"
                        v-model="technicalInfo.engineRoomPosition"
                        outlined
                        :label="$t('book/technical-info:labels.engine-room-position')"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :items="restEngineRoomPositions"
                        clearable
                        item-value="name"
                        item-text="translatedName"
                    />

                    <v-textarea
                        v-if="technicalInfo.engineRoomExists"
                        v-model="technicalInfo.engineRoomPositionInfo"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.engine-room-position-info')"
                    />
                  </v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <BaseButton
                        color="primary"
                        :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                        @clickedButton="technicalPanels++"
                    />
                  </v-col>
                  <v-col cols="12" md="5"></v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Emergency Panel -->
            <v-expansion-panel ref="panel_7" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="primary--text mb-0">
                      {{ $t('book/technical-info:titles.emergency') }}
                    </h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(technicalPanels === 7) ? technicalPanels = null : technicalPanels = 7"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.insert-data') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-select
                        v-model="technicalInfo.emergencyType"
                        outlined
                        :label="$t('book/technical-info:labels.emergency-type')"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :items="restEmergencyType"
                        clearable
                        multiple
                        chips
                        item-text="translatedName"
                        item-value="name"
                        deletable-chips
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-checkbox
                        v-model="technicalInfo.emergencyCallSystemGSM"
                        :label="$t('book/technical-info:labels.emergency-GSM')"
                        color="secondary"
                    />

                    <v-checkbox
                        v-if="technicalInfo.emergencyCallSystemGSM"
                        v-model="technicalInfo.emergencyAnalog"
                        :label="$t('book/technical-info:labels.emergency-analog')"
                        color="secondary"
                    />

                    <v-text-field
                        v-if="technicalInfo.emergencyCallSystemGSM"
                        v-model="technicalInfo.emergencyPhoneNumber"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.emergency-phone')"
                    />

                    <v-textarea
                        v-if="technicalInfo.emergencyCallSystemGSM"
                        v-model="technicalInfo.emergencyCallSystemInfo"
                        outlined
                        clearable
                        :label="$t('book/technical-info:labels.emergency-info')"
                    />
                  </v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <BaseButton
                        color="primary"
                        :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                        @clickedButton="technicalPanels++"
                    />
                  </v-col>
                  <v-col cols="12" md="5"></v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Cabin panel -->
            <v-expansion-panel ref="panel_8" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="primary--text mb-0">{{ $t('book/technical-info:titles.cabin') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(technicalPanels === 8) ? technicalPanels = null : technicalPanels = 8"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.insert-data') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-text-field
                        v-model="technicalInfo.cabinWidth"
                        outlined
                        clearable
                        type="number"
                        :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                        :label="$t('book/technical-info:labels.cabin-width')"
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-text-field
                        v-model="technicalInfo.cabinDepth"
                        outlined
                        clearable
                        type="number"
                        :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                        :label="$t('book/technical-info:labels.cabin-depth')"
                    />
                  </v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <h6 class="primary--text">Kabinentür</h6>
                    <v-select
                        v-model="technicalInfo.cabinDoorType"
                        outlined
                        :label="$t('book/technical-info:labels.cabin-door-type')"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :items="restDoorType"
                        clearable
                        item-value="name"
                        item-text="translatedName"
                    />
                  </v-col>
                  <v-col cols="12" md="5" class="d-none d-md-block"></v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-text-field
                        v-model="technicalInfo.cabinDoorWidth"
                        outlined
                        clearable
                        type="number"
                        :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                        :label="$t('book/technical-info:labels.cabin-door-width')"
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-text-field
                        v-model="technicalInfo.cabinDoorHeight"
                        outlined
                        clearable
                        type="number"
                        :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                        :label="$t('book/technical-info:labels.cabin-door-height')"
                    />
                  </v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <BaseButton
                        color="primary"
                        :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                        @clickedButton="technicalPanels++"
                    />
                  </v-col>
                  <v-col cols="12" md="5"></v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Shaft Panel -->
            <v-expansion-panel ref="panel_9" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="primary--text mb-0">{{ $t('book/technical-info:titles.shaft') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(technicalPanels === 9) ? technicalPanels = null : technicalPanels = 9"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.insert-data') }}
                    </v-btn>
                  </v-col>
                </v-row>

              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-select
                        v-model="technicalInfo.shaftProtectionType"
                        outlined
                        :label="$t('book/technical-info:labels.shaft-protection-type')"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :items="restProtectionType"
                        clearable
                        item-value="name"
                        item-text="translatedName"
                    />
                  </v-col>
                  <v-col cols="12" md="5" class="d-none d-md-block"></v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <h6 class="primary--text">Schachttür</h6>
                    <v-select
                        v-model="technicalInfo.shaftDoorType"
                        outlined
                        :label="$t('book/technical-info:labels.shaft-door-type')"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :items="restDoorType"
                        clearable
                        item-value="name"
                        item-text="translatedName"
                    />
                  </v-col>
                  <v-col cols="12" md="5" class="d-none d-md-block"></v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <v-text-field
                        v-model="technicalInfo.shaftDoorWidth"
                        outlined
                        clearable
                        type="number"
                        :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                        :label="$t('book/technical-info:labels.shaft-door-width')"
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-text-field
                        v-model="technicalInfo.shaftDoorHeight"
                        outlined
                        clearable
                        type="number"
                        :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                        :label="$t('book/technical-info:labels.shaft-door-height')"
                    />
                  </v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <BaseButton
                        color="primary"
                        :btn-text="$t('book/public:qrcode.onboarding.btns.to-status-section')"
                        @clickedButton="statePanels = 0"
                    />
                  </v-col>
                  <v-col cols="12" md="5"></v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <h4 class="font-weight-bold primary--text mt-5 mb-2">{{ $t('book/public:qrcode.common.state') }}</h4>
          <v-expansion-panels flat tile class="d-block" v-model="statePanels">

            <!-- Documentation Panel -->
            <v-expansion-panel ref="state_panel_0" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="mb-0 primary--text">{{ $t('book/public:qrcode.data.state.panel-titles.docu') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(statePanels === 0) ? statePanels = null : statePanels = 0"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.check-data') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <RuleStateTable
                    ref="ruleStateTable"
                    :header="docuHeaders"
                />
                <BaseButton
                    class="mt-5"
                    color="primary"
                    :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                    @clickedButton="statePanels++"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Car Panel -->
            <v-expansion-panel ref="state_panel_1" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="mb-0 primary--text">{{ $t('book/public:qrcode.data.state.panel-titles.car') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(statePanels === 1) ? statePanels = null : statePanels = 1"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.check-data') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <StateTable :data="state" :header="docuHeaders" :fields="carHeaders" :facility-state="state"/>
                <BaseButton
                    class="mt-5"
                    color="primary"
                    :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                    @clickedButton="statePanels++"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Stop Panel -->
            <v-expansion-panel ref="state_panel_2" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="mb-0 primary--text">{{ $t('book/public:qrcode.data.state.panel-titles.stop') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(statePanels === 2) ? statePanels = null : statePanels = 2"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.check-data') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <StateTable :data="state" :header="docuHeaders" :fields="stopHeaders" :facility-state="state"/>
                <BaseButton
                    class="mt-5"
                    color="primary"
                    :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                    @clickedButton="statePanels++"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Engine Room Panel -->
            <v-expansion-panel ref="state_panel_3" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="mb-0 primary--text">{{ $t('book/public:qrcode.data.state.panel-titles.engine-room') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(statePanels === 3) ? statePanels = null : statePanels = 3"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.check-data') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <StateTable :data="state" :header="docuHeaders" :fields="erHeaders" :facility-state="state"/>
                <BaseButton
                    class="mt-5"
                    color="primary"
                    :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                    @clickedButton="statePanels++"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Shaft Panel -->
            <v-expansion-panel ref="state_panel_4" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="mb-0 primary--text">{{ $t('book/public:qrcode.data.state.panel-titles.shaft') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(statePanels === 4) ? statePanels = null : statePanels = 4"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.check-data') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <StateTable :data="state" :header="docuHeaders" :fields="shaftHeaders" :facility-state="state"/>
                <BaseButton
                    class="mt-5"
                    color="primary"
                    :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                    @clickedButton="statePanels++"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Shaft Pit Panel -->
            <v-expansion-panel ref="state_panel_5" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="mb-0 primary--text">{{ $t('book/public:qrcode.data.state.panel-titles.shaft-pit') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(statePanels === 5) ? statePanels = null : statePanels = 5"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.check-data') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <StateTable :data="state" :header="docuHeaders" :fields="shaftPitHeaders" :facility-state="state"/>
                <BaseButton
                    class="mt-5"
                    color="primary"
                    :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                    @clickedButton="statePanels++"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Image Panel -->
            <v-expansion-panel ref="state_panel_6" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="mb-0 primary--text">{{ $t('book/public:qrcode.onboarding.image-section') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(statePanels === 6) ? statePanels = null : statePanels = 6"
                    >
                      {{ $t('buttons:upload-specific', { specific: $t('common:image_plural')}) }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <RuleStateTable
                    ref="pictureTable"
                    :header="pictureHeaders"
                    is-picture-state
                />
                <BaseButton
                    class="mt-5"
                    color="primary"
                    :btn-text="$t('book/public:qrcode.onboarding.btns.next-section')"
                    @clickedButton="statePanels++"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Summary panel -->
            <v-expansion-panel ref="state_panel_7" class="mb-2">
              <v-expansion-panel-header>
                <v-row justify="space-between" align="center">
                  <v-col cols="12" sm="5" class="py-0">
                    <h6 class="mb-0 primary--text">{{ $t('book/public:qrcode.data.state.panel-titles.summary') }}</h6>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-sm-0 text-sm-right mr-sm-5">
                    <v-btn
                        rounded
                        color="primary"
                        elevation="0"
                        class="no-text-transform white--text"
                        small
                        @click.native.stop="(statePanels === 7) ? statePanels = null : statePanels = 7"
                    >
                      {{ $t('book/public:qrcode.onboarding.btns.summary') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager class="mt-2">
                <v-textarea
                    outlined
                    v-model="state.summary"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <div class="mt-5">
            <BaseButton
                color="grey"
                :btnText="$t('buttons:cancel')"
                @clickedButton="cancel"
                :lower-case="$vuetify.breakpoint.xsOnly"
            />
            <BaseButton
                color="primary"
                :btnText="$vuetify.breakpoint.xsOnly ?
                    $t('book/public:qrcode.onboarding.btns.short-finish') :
                    $t('book/public:qrcode.onboarding.btns.finish-onboarding')"
                @clickedButton="finishOnboarding"
                :lower-case="$vuetify.breakpoint.xsOnly"
            />
          </div>
        </v-form>
      </v-card-text>
    </v-card>

    <QRCodeFacilityOnboardingSidebar
        v-if="!isLoading"
        @scrollToTechnic="scrollTo($event, 'technic')"
        @scrollToState="scrollTo($event, 'state')"
    />

    <ExternalFacilityDialog ref="facilityDialog"/>
  </div>
</template>
<script>
import TaskCard from "@/modules/book/components/TaskCard/TaskCard";
import QRCodeService from "@/modules/book/services/qrcode-service";
import DataUtils from "@/modules/core/services/data-utils";
import Building from "@/modules/book/data/building";
import Facility from "@/modules/book/data/facility";
import TechnicalInfo from "@/modules/book/data/technical-info";
import ElevatorState from "@/modules/book/data/elevator-state";
import LabelField from "@/modules/core/components/LabelField";
import InfoService from "@/modules/core/services/info-service";
import Utils from "@/modules/core/services/utils";
import EnumService from "@/modules/core/services/enum-service";
import CustomerTypeahead from "@/modules/core/components/CustomerTypeAhead";
import {ruleNotNegative} from "@/modules/core/services/rules";
import DateField from "@/modules/core/components/DateField";
import StateTableUtils from "@/modules/core/services/state-table-utils";
import StateTable from "@/modules/core/components/DataTable/StateTable";
import BaseButton from "@/modules/core/components/Base/BaseButton";
import UsersService from "@/modules/core/services/users-service";
import Users from "@/modules/core/data/users";
import CustomerService from "@/modules/core/services/customer-service";
import Customer from "@/modules/core/data/customer";
import FullHeightRadialLoader from "@/modules/core/components/Loader/FullHeightRadialLoader";
import FacilityDocumentation from "@/modules/book/data/facility-documentation";
import QRCodeFacilityOnboardingSidebar from "@/modules/book/views/public/QRCodeFacilityOnboardingSidebar";
import {mapGetters} from "vuex";
import RuleStateTable from "@/modules/core/components/DataTable/RuleStateTable";
import indexedDBService from '@/modules/core/services/indexedDB-service';
import DocumentationService from "@/modules/core/services/documentation-service";
import Vue from "vue";
import DateUtils from "@/modules/core/services/date-utils";
import ExternalFacilityDialog from "@/modules/book/components/Facility/ExternalFacilityDialog";
import FacilityConnectionView from "@/modules/book/components/Facility/FacilityConnectionView.vue";

export default {
  name: 'QRCodeFacilityOnboarding',
  components: {
    FacilityConnectionView,
    ExternalFacilityDialog,
    QRCodeFacilityOnboardingSidebar,
    FullHeightRadialLoader,
    BaseButton,
    StateTable,
    DateField,
    CustomerTypeahead,
    LabelField,
    TaskCard,
    RuleStateTable
  },
  data() {
    return {
      building: new Building(),
      facility: new Facility(),
      technicalInfo: new TechnicalInfo(),
      state: new ElevatorState(),
      facilityDocumentations: [],
      facilityPictures: [],
      user: new Users(),
      customer: new Customer(),
      currentDate: "",

      // options for elevator type e.g. OTHER or SELF_DRIVING
      restElevatorType: [],
      // options for drive type e.g. HYDRAULIC or ROPE
      restDrives: [],
      // options for engine room position e.g. BOTTOM or TOP
      restEngineRoomPositions: [],
      // options for door position e.g. ONE_SIDED or CORNER
      restDoorPositions: [],
      // options for door security type e.g. LIGHT_GRID or SENSOR_BAR
      restDoorSecurityType: [],
      // options for door type e.g. FOLDING_DOOR or MULTIPLE_PART
      restDoorType: [],
      // options for emergency type e.g. HORN or EMERGENCY_CALL_SYSTEM
      restEmergencyType: [],
      // options for hydraulic drive types e.g. DIRECT or INDIRECT
      restHydraulicDriveType: [],
      // options for rope drive type e.g. TWO_SPEED or GEARLESS
      restRopeDriveType: [],
      // options for protection type e.g. CONCRETE or WOOD
      restProtectionType: [],

      // state headers for state groups (e.g. car = "Fahrkorb"), which includes
      // translatedName, successField, noteField, docuField and original enum
      // see state-table-utill.js for further explanation
      carHeaders: [],
      stopHeaders: [],
      erHeaders: [],
      shaftHeaders: [],
      shaftPitHeaders: [],

      isLoading: true,
      isSaving: false,

      technicalPanels: null,
      statePanels: null,

      restConnectableFacilities: [],
      facilitySearch: '',
    }
  },
  methods: {
    ruleNotNegative,

    ...mapGetters('auth', ['isAdmin', 'isWorker']),

    showFacility() {
      if (this.isWorker) {
        // show basic facility dialog
        this.$refs.facilityDialog.initDialog(this.facility.id);
      } else {
        // route to facility
        this.$router.push(`/${this.$store.state.manager.bookModuleUrl}/facilities/edit/${this.facility.id}`)
      }
    },

    // TODO: not always scrolling to right place
    scrollTo(ref, panelType) {
      if (panelType && 'ref' in ref && 'index' in ref) {
        if (panelType === 'technic') {
          this.technicalPanels = ref.index;
        } else if(panelType === 'state') {
          this.statePanels = ref.index;
        }

        setTimeout(() => {
          this.$vuetify.goTo(this.$refs[ref.ref], {duration: 1000, offset: 100, easing: 'easeInOutCubic'})
        }, 250);
      } else if (!ref.includes('undefined')) {
        setTimeout(() => {
          this.$vuetify.goTo(this.$refs[ref], {duration: 1000, offset: 100, easing: 'easeInOutCubic'})
        }, 250);
      }
    },

    getSubDrive(drive) {
      if (drive === 'HYDRAULIC') {
        this.getEnum(
            "elevator/drive/hydraulic",
            "enums:technical.elevator-hydraulic-drive-types.",
            this.restHydraulicDriveType,
            "elevator hydraulic drive types",
            "hydraulic"
        )
      } else if (drive === 'ROPE') {
        this.getEnum(
            "elevator/drive/rope",
            "enums:technical.elevator-rope-drive-types.",
            this.restRopeDriveType,
            "elevator rope drive types",
            "rope"
        )
      }
    },

    /**
     * Reads and translates enums like elevator types
     */
    getEnum(enumPath, translationPath, enumArray, failureString, failureTranslationPath) {
      InfoService.readAndTranslateEnum(
          enumPath,
          translationPath,
          (res) => {
            res.forEach((element) => {
              enumArray.push(element)
            })
          },
          (error) => {
            this.$logger.error("Failed to read " + failureString + " , because: " + error);
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('book/technical-info:snackbar. ' + failureTranslationPath)}))
          }
      )
    },

    // read the values for each state category (translatedName, successField, noteField, docuField, enum
    translateAndGetElevatorStateFields() {
      this.carHeaders = StateTableUtils.getElevatorStateCarFieldsAndTranslation();
      this.stopHeaders = StateTableUtils.getElevatorStateStopFieldsAndTranslation();
      this.erHeaders = StateTableUtils.getElevatorStateERFieldsAndTranslation();
      this.shaftHeaders = StateTableUtils.getElevatorStateShaftFieldsAndTranslation();
      this.shaftPitHeaders = StateTableUtils.getElevatorStateShaftPitFieldsAndTranslation();
    },

    /**
     *  post all documents from a state and wait till all are posted
     */
    async postDocusFromStates(stateHeader) {
      let promises = [];

      // loop through all documentation-fields of the state category
      // e.g. carHeader: carRideComfortDocs, carDoorRunPropsDocs ...
      for (let i = 0; i < stateHeader[3].values.length; i++) {
        let currentState = stateHeader[3].values[i];

        // e.g. carRideComfortDocs loop through it
        // this.state[carRideComfortDocs]
        for (let j = 0; j <= this.state[currentState].length; j++) {
          let currentDocument = this.state[currentState][j]

          // if the document has the flag "toSave" set to true
          // we save it in the backend
          if (currentDocument && currentDocument.toSave) {
            let facilityDocu = {};
            facilityDocu.name = currentDocument.name;
            facilityDocu.start = currentDocument.start;
            facilityDocu.end = currentDocument.end;
            facilityDocu.type = currentDocument.type;

            promises.push(
                new Promise(resolve => {
                  QRCodeService.postFacilityDocumentationToAPI(
                      this.$route.params.code,
                      currentDocument.file,
                      facilityDocu,
                      (res) => {
                        this.state[currentState][j] = res.data;
                        resolve(this.state[currentState][j])
                      }
                  )
                })
            )
          }
        }
      }

      await Promise.all(promises)
    },

    /**
     *  post all documents from the required pictures and wait till all are posted
     */
    async postRequiredPictures() {
      let promises = [];

      // loop through all requiredPictures of the state
      // e.g. CABIN_IMAGE
      for (let i = 0; i < this.state.requiredPictures.length; i++) {
        // loop through all documentations of the picture
        for (let j = this.state.requiredPictures[i].documents.length - 1; j >= 0; j--) {
          let currentDocument = this.state.requiredPictures[i].documents[j];
          // if the document has the flag "toSave" set to true
          // we save it in the backend
          if (currentDocument && currentDocument.toSave) {
            let facilityDocu = {};
            facilityDocu.name = currentDocument.name;
            facilityDocu.start = currentDocument.start;
            facilityDocu.end = currentDocument.end;
            facilityDocu.type = currentDocument.type;

            promises.push(new Promise(resolve => {
              QRCodeService.postFacilityDocumentationToAPI(
                  this.$route.params.code,
                  currentDocument.file,
                  facilityDocu,
                  (res) => {
                    this.state.requiredPictures[i].documents[j] = res.data;
                    resolve(this.state.requiredPictures[i].documents[j])
                  }
              )
            }))

          }
        }
      }

      await Promise.all(promises)
    },

    /**
     *  post all documents from the rule states and wait till all are posted
     */
    async postRuleStateDocuments() {
      let promises = [];

      // loop through all rule States e.g. SYSTEM_OWNER_MANUAL
      for (let i = 0; i < this.state.ruleStates.length; i++) {
        let currentRuleState = this.state.ruleStates[i];
        // loop through all documentations of this rule state
        for (let j = currentRuleState.documents.length - 1; j >= 0; j--) {
          let currentDocumentation = currentRuleState.documents[j];
          // if the document has the flag "toSave" set to true
          // we save it in the backend
          if (currentDocumentation && currentDocumentation.toSave) {
            let facilityDocu = {};
            facilityDocu.name = currentDocumentation.name;
            facilityDocu.start = currentDocumentation.start;
            facilityDocu.end = currentDocumentation.end;
            facilityDocu.type = currentDocumentation.type;

            promises.push(new Promise(resolve => {
              QRCodeService.postFacilityDocumentationToAPI(
                  this.$route.params.code,
                  currentDocumentation.file,
                  facilityDocu,
                  (res) => {
                    this.state.ruleStates[i].documents[j] = res.data;
                    resolve(this.state.ruleStates[i].documents[j])
                  }
              )
            }))
          }
        }
      }

      await Promise.all(promises);
    },

    /**
     * Delete required pictures documentations with the tag toDelete
     */
    async deleteRequiredPictures() {
      let promises = [];

      // loop through all requiredPictures of the state
      // e.g. CABIN_IMAGE
      for (let i = 0; i < this.state.requiredPictures.length; i++) {
        // loop through all documentations of the picture
        for (let j = this.state.requiredPictures[i].documents.length - 1; j >= 0; j--) {
          let currentDocument = this.state.requiredPictures[i].documents[j];
          // if the document has the flag "toDelete" set to true
          if (currentDocument && currentDocument.toDelete && !currentDocument.toSave) {
            // if the document has an id (so was previously posted to backend)
            // delete it from backend
            if (currentDocument.id) {
              promises.push(new Promise(resolve => {
                DocumentationService.delete(currentDocument.id,
                    () => {
                      this.state.requiredPictures[i].documents.splice(j, 1);
                      resolve()
                    }
                )
              }))
              // else just remove it from the array
            } else {
              this.state.requiredPictures[i].documents.splice(j, 1);
            }
          }
        }
      }

      await Promise.all(promises)
    },

    /**
     * Delete rule state documentations with the tag toDelete
     */
    async deleteRuleStateDocuments() {
      let promises = [];

      // loop through all rule States e.g. SYSTEM_OWNER_MANUAL
      for (let i = 0; i < this.state.ruleStates.length; i++) {
        let currentRuleState = this.state.ruleStates[i];
        // loop through all documentations of this rule state
        for (let j = currentRuleState.documents.length - 1; j >= 0; j--) {
          let currentDocumentation = currentRuleState.documents[j];
          // if the document has the flag "toDelete" set to true
          if (currentDocumentation && currentDocumentation.toDelete && !currentDocumentation.toSave) {
            // if the document has an id (so was previously posted to backend)
            // delete it from backend
            if (currentDocumentation.id) {

              promises.push(resolve => {
                DocumentationService.delete(currentDocumentation.id,
                    () => {
                      this.state.ruleStates[i].documents.splice(j, 1);
                      resolve()
                    }
                )
              })
              // else just remove it from the array
            } else {
              this.state.ruleStates[i].documents.splice(j, 1);
            }
          }
        }
      }

      await Promise.all(promises)
    },

    /**
     * Delete state documentations with the tag toDelete
     */
    async deleteDocusFromState(stateHeader) {
      let promises = [];

      // loop through all documentation-fields of the state category
      // e.g. carHeader: carRideComfortDocs, carDoorRunPropsDocs ...
      for (let i = 0; i < stateHeader[3].values.length; i++) {
        let currentState = stateHeader[3].values[i];

        // e.g. carRideComfortDocs loop through it
        // this.state[carRideComfortDocs]
        for (let j = this.state[currentState].length - 1; j >= 0; j--) {
          let currentDocument = this.state[currentState][j]
          // if the document has the flag "toDelete" set to true and if the document has an id (so was previously posted to backend)
          // delete it from backend
          if (currentDocument && currentDocument.id && currentDocument.toDelete && !currentDocument.toSave) {
            promises.push(new Promise(resolve => {
              DocumentationService.delete(currentDocument.id,
                () => {
                  this.state[stateHeader[3].values[i]].splice(j, 1);
                  resolve()
                }
              )
            }))
            // else just remove it from the array
          } else if (currentDocument && currentDocument.toDelete && !currentDocument.toSave) {
            this.state[stateHeader[3].values[i]].splice(j, 1);
          }
        }
      }

      await Promise.all(promises);
    },

    finishOnboarding() {
      // if the user has a internet connection
      // he can post everything
      if (window.navigator.onLine) {
        this.isSaving = true;
        // we need to wait for every previous delete/post documentation loops
        // because newly added facilityDocumentations has to be saved
        // before we save the onboarding

        // delete all documentations from the category required pictures where the docus have the tag "toDelete"
        this.deleteRequiredPictures().then(() => {
          // delete all documentations from the category rule state where the docus have the tag "toDelete"
          this.deleteRuleStateDocuments().then(() => {
            // delete all documentations from the state category car ("Fahrkorb") where the docus have the tag "toDelete"
            this.deleteDocusFromState(this.carHeaders).then(() => {
              // delete all documentations from the category stop ("An jeder Haltestelle") where the docus have the tag "toDelete"
              this.deleteDocusFromState(this.stopHeaders).then(() => {
                // delete all documentations from the category er ("Maschinenraum") where the docus have the tag "toDelete"
                this.deleteDocusFromState(this.erHeaders).then(() => {
                  // delete all documentations from the category shaft ("Schacht") where the docus have the tag "toDelete"
                  this.deleteDocusFromState(this.shaftHeaders).then(() => {
                    // delete all documentations from the category shaft pit ("Schachtgrube") where the docus have the tag "toDelete"
                    this.deleteDocusFromState(this.shaftPitHeaders).then(() => {

                      // post all documents from the category required pictrues where the docus have the tag "toSave"
                      this.postRequiredPictures().then(() => {
                        // post all documents from the category rule state where the docus have the tag "toSave"
                        this.postRuleStateDocuments().then(() => {
                          // post all documentations from the state category car ("Fahrkorb") where the docus have the tag "toSave"
                          this.postDocusFromStates(this.carHeaders).then(() => {
                            // post all documentations from the category stop ("An jeder Haltestelle") where the docus have the tag "toSave"
                            this.postDocusFromStates(this.stopHeaders).then(() => {
                              // post all documentations from the category er ("Maschinenraum") where the docus have the tag "toSave"
                              this.postDocusFromStates(this.erHeaders).then(() => {
                                // post all documentations from the category shaft ("Schacht") where the docus have the tag "toSave"
                                this.postDocusFromStates(this.shaftHeaders).then(() => {
                                  // post all documentations from the category shaft pit ("Schachtgrube") where the docus have the tag "toSave"
                                  this.postDocusFromStates(this.shaftPitHeaders).then(() => {
                                    // finally save the whole facilityState and facilityInfo
                                    QRCodeService.saveOnboarding(this.facility, this.technicalInfo, this.state,
                                        () => {

                                          Utils.showSuccessSnackbar(this.$t('snackbar:edit-success', {name: this.$t('book/public:qrcode.common.onboarding')}));

                                          // remove the state and technicalInfo from the indexedDB
                                          indexedDBService.deleteFacilityState(this.state);
                                          indexedDBService.deleteTechnicalInfo(this.technicalInfo);
                                          this.isSaving = false;
                                          this.$router.go(-1);
                                        },
                                        (error) => {
                                          this.isSaving = false;
                                          this.$logger.error("Failed to finish facility onboarding for facility " + this.facility.id +", because: " + error);
                                          Utils.showFailureSnackbar(this.$t('snackbar:edit-failure', {name: this.$t('book/public:qrcode.common.onboarding')}))
                                        }
                                    )
                                  })
                                })
                              })
                            })
                          })
                        })
                      })
                    })
                  })
                })
              })
            })
          })
        })
      } else {
        Utils.showWarningSnackbar(this.$t('book/public:qrcode.snackbar.no-internet'))
      }
    },

    /**
     * fill up facilityDocumentation to ruleState documentations
     * if the user has already uploaded some required documentations
     * outside of the onboarding
     */
    async fillRequiredDocumentations() {
      let promises = [];

      // loop through all ruleStates e.g. "Betreiber-Handbuch"
      for (let i = 0; i < this.state.ruleStates.length; i++) {
        this.state.ruleStates[i].documents = [];

        // loop through all facilityDocumentations of this facility
        for (let j = 0; j < this.facilityDocumentations.length; j++) {
          // if the ruleState name (e.g. SYSTEM_OWNER_MANUAL) matches with the type of the facilityDocumentation
          // add this documentation to the "Betreiber-Handbuch" documentation array
          if (this.state.ruleStates[i].name === this.facilityDocumentations[j].type) {
            Vue.set(this.state.ruleStates[i].documents, this.state.ruleStates[i].documents.length, this.facilityDocumentations[j])
          }
        }

        // if there are more than 0 documentation in this state
        // set the state to success
        if (this.state.ruleStates[i].documents.length > 0) {
          Vue.set(this.state.ruleStates[i], "success", true);
        } else {
          Vue.set(this.state.ruleStates[i], "success", false);
        }
      }

      // get documentation files so we can display the documentation
      for (let rule of this.state.ruleStates) {
        if (rule.documents.length > 0) {
          for (let document of rule.documents) {
            document.localId = Utils.generateUUIDv4();

            promises.push(new Promise(resolve => {
              DocumentationService.getDocByTypeAndId(
                  document.id,
                  document.fileType,
                  document.documentType,
                  document.facilityId,
                  (res) => {
                    Vue.set(document, "src", res);
                    indexedDBService.saveFacilityState(this.state);
                    resolve(document)
                  },
                  (error) => {
                    this.$logger.error("Failed loading documentation source for document " + document.id +", because: " + error);
                  }
              )
            }))
          }
        }
      }

      await Promise.all(promises);
    },

    /**
     * fill up facilityDocumentation to requiredPictures documentations
     * if the user has already uploaded some facility pictures
     * outside of the onboarding
     */
    async fillRequiredPictures() {
      let promises = [];

      // loop through all requiredPictures e.g. SHAFT_DOOR_IMAGE (="Schachttür)
      for (let i = 0; i < this.state.requiredPictures.length; i++) {
        this.state.requiredPictures[i].documents = [];

        // loop through all facilityDocumentations of this facility
        for (let j = 0; j < this.facilityDocumentations.length; j++) {
          // if the required picture name (e.g. SHAFT_DOOR_IMAGE) matches with the type of the facilityDocumentation
          // add this documentation to the SHAFT_DOOR_IMAGE documentation array
          if (this.state.requiredPictures[i].name === this.facilityDocumentations[j].type) {
            Vue.set(this.state.requiredPictures[i].documents, this.state.requiredPictures[i].documents.length, this.facilityDocumentations[j])
          }
        }
      }

      // get documentation files so we can display the documentation
      for (let picture of this.state.requiredPictures) {
        if (picture.documents.length > 0) {
          for (let document of picture.documents) {
            document.localId = Utils.generateUUIDv4();

            promises.push(new Promise(resolve => {
              DocumentationService.getDocByTypeAndId(
                  document.id,
                  document.fileType,
                  document.documentType,
                  document.facilityId,
                  (res) => {
                    Vue.set(document, "src", res);
                    indexedDBService.saveFacilityState(this.state);
                    resolve(document)
                  },
                  (error) => {
                    this.$logger.error("Failed loading documentation source for document " + document.id +", because: " + error);
                  }
              )
            }))
          }
        }
      }

      await Promise.all(promises);
    },

    fillRuleAndPictureTable() {
      // fill the ruleState table with data
      this.$refs.ruleStateTable.facilityState = this.state;
      this.$refs.ruleStateTable.rulesOrPictures = this.state.ruleStates;

      // fill the required picture table with data
      this.$refs.pictureTable.facilityState = this.state;
      this.$refs.pictureTable.rulesOrPictures = this.state.requiredPictures;
    },

    getAdditionalNames(building, facility) {
      return [
        {name: building.name, asButton: false},
        {name: facility.name.facilityName, asButton: false}
      ];
    },

    cancel() {
      indexedDBService.clearObjectStore('facilityState');
      indexedDBService.clearObjectStore('technicalInfo');
      this.$router.go(-1);
    }
  },
  computed: {
    currentLang() {
      return this.$store.state.translation.language;
    },

    // returns table headers for ruleState and normal state table
    docuHeaders() {
      return [
        this.$t('book/public:qrcode.onboarding.docu-headers.check'),
        this.$t('common:status'),
        this.$t('common:comment'),
        this.$t('book/public:qrcode.onboarding.docu-headers.action'),
        this.$t('common:document_plural')
      ]
    },

    // returns table headers for required pictures table
    pictureHeaders() {
      return [
          this.$t('common:name'),
          this.$t('book/public:qrcode.onboarding.docu-headers.action'),
          this.$t('common:image_plural')
      ]
    },

    getShaftMineWarningText() {
      if (this.technicalInfo.reducedShaftHead && !this.technicalInfo.reducedShaftPit) {
        return this.$t('book/technical-info:shaft-mine.only-head');
      } else if (!this.technicalInfo.reducedShaftHead && this.technicalInfo.reducedShaftPit) {
        return this.$t('book/technical-info:shaft-mine.only-pit');
      } else if (this.technicalInfo.reducedShaftHead && this.technicalInfo.reducedShaftPit) {
        return this.$t('book/technical-info:shaft-mine.head-and-pit');
      } else {
        return ''
      }
    }
  },
  watch: {
    // will be called if language changed
    currentLang() {
      Utils.sleep(500).then(() => {
        this.$refs.form.resetValidation()

        // translate and sort enums
        EnumService.translateAndSortEnumObject(this.restElevatorType, "enums:technical.elevator-type.")
        EnumService.translateAndSortEnumObject(this.restDrives, "enums:technical.drive.")
        EnumService.translateAndSortEnumObject(this.restEngineRoomPositions, "enums:technical.engine-room-position.")
        EnumService.translateAndSortEnumObject(this.restDoorPositions, "enums:technical.elevator-door-position.")
        EnumService.translateAndSortEnumObject(this.restDoorSecurityType, "enums:technical.elevator-door-security-types.")
        EnumService.translateAndSortEnumObject(this.restDoorType, "enums:technical.elevator-door-types.")
        EnumService.translateAndSortEnumObject(this.restEmergencyType, "enums:technical.elevator-emergency-types.")
        EnumService.translateAndSortEnumObject(this.restHydraulicDriveType, "enums:technical.elevator-hydraulic-drive-types.")
        EnumService.translateAndSortEnumObject(this.restRopeDriveType, "enums:technical.elevator-rope-drive-types.")
        EnumService.translateAndSortEnumObject(this.restProtectionType, "enums:technical.elevator-protection-types.")
        EnumService.translateAndSortEnumObject(this.state.ruleStates, "enums:facility-documentation-types.")

        this.translateAndGetElevatorStateFields();
      });
    },

    // scroll to panel which is opening
    // set a timer, so don't weird scrolling behaviour is happening
    technicalPanels() {
      this.scrollTo('panel_' + this.technicalPanels);
    },

    statePanels() {
      this.scrollTo('state_panel_' + this.statePanels);
    },

    //if specific emergencyType enums are selected
    //set the according flag
    'technicalInfo.emergencyType' (val) {
      if (val && val.includes('GSM_EMERGENCY_CALL_SYSTEM')) {
        this.technicalInfo.emergencyCallSystemGSM = true;
      } else {
        this.technicalInfo.emergencyCallSystemGSM = false;
      }

      if (val && val.includes('ANALOG_SYSTEM')) {
        this.technicalInfo.emergencyAnalog = true;
      } else {
        this.technicalInfo.emergencyAnalog = false;
      }

      if (val && val.includes('AWM_MODULE')) {
        this.technicalInfo.awmModule = true;
      } else {
        this.technicalInfo.awmModule = false;
      }
    },

    //if the gsm emergency call system checkbox was clicked
    // remove or push gsm emergency call system from the emergencyTypes
    // according to checkbox state
    'technicalInfo.emergencyCallSystemGSM' (val) {
      if (val && this.technicalInfo.emergencyType && !this.technicalInfo.emergencyType.includes('GSM_EMERGENCY_CALL_SYSTEM')) {
        this.technicalInfo.emergencyType.push('GSM_EMERGENCY_CALL_SYSTEM')
      } else if (!val && this.technicalInfo.emergencyType) {
        for (let i = this.technicalInfo.emergencyType.length - 1; i >= 0 ; i--) {
          if (this.technicalInfo.emergencyType[i] === 'GSM_EMERGENCY_CALL_SYSTEM') {
            this.technicalInfo.emergencyType.splice(i, 1);
          }
        }
      }
    },

    // if the emergency analog checkbox was clicked
    // remove or push emergency analog from the emergencyTypes
    // according to checkbox state
    'technicalInfo.emergencyAnalog' (val) {
      if (val && this.technicalInfo.emergencyType && !this.technicalInfo.emergencyType.includes('ANALOG_SYSTEM')) {
        this.technicalInfo.emergencyType.push('ANALOG_SYSTEM')
      } else if (!val && this.technicalInfo.emergencyType) {
        for (let i = this.technicalInfo.emergencyType.length - 1; i >= 0 ; i--) {
          if (this.technicalInfo.emergencyType[i] === 'ANALOG_SYSTEM') {
            this.technicalInfo.emergencyType.splice(i, 1);
          }
        }
      }
    },

    // if awmModule checkbox was clicked
    // remove or push awmModule from the emergencyTypes
    // according to checkbox state
    'technicalInfo.awmModule' (val) {
      if (val && this.technicalInfo.emergencyType && !this.technicalInfo.emergencyType.includes('AWM_MODULE')) {
        this.technicalInfo.emergencyType.push('AWM_MODULE')
      } else if (!val && this.technicalInfo.emergencyType) {
        for (let i = this.technicalInfo.emergencyType.length - 1; i >= 0 ; i--) {
          if (this.technicalInfo.emergencyType[i] === 'AWM_MODULE') {
            this.technicalInfo.emergencyType.splice(i, 1);
          }
        }
      }
    },

    // if even a single field of technical info changes
    // save the whole technicalInfo to the indexedDB
    technicalInfo: {
      handler(newTechnicalInfo, oldTechnicalInfo) {
        if (oldTechnicalInfo.facility) {
          indexedDBService.readTechnicalInfo(oldTechnicalInfo)
              .then((res) => {
                // if the technicalInfo (saved in indexedDB) is different to the
                // new technicalInfo, then save the newTechnicalInfo to the indexedDB
                // so the changes will be updated, when a field from the technicalInfo changes
                if (JSON.stringify(newTechnicalInfo) !== JSON.stringify(res)) {
                  indexedDBService.saveTechnicalInfo(newTechnicalInfo);
                }
              })
        }
      },
      deep: true
    },

    // if even a single field of state changes
    // save the whole state to the indexedDB
    state: {
      handler(oldState, newState) {
        if (oldState.facility && newState.facility) {
          if (newState.requiredPictures.filter(e => 'documents' in e).length > 0 && newState.ruleStates.filter(e => 'documents' in e).length > 0) {
            indexedDBService.readFacilityState(oldState)
                .then((res) => {
                  if (JSON.stringify(newState) !== JSON.stringify(res)) {
                    indexedDBService.saveFacilityState(newState)
                  }
                })
          }
        }
      },
      deep: true
    }
  },
  created() {
    QRCodeService.readDataFromPublic(this.$route.params.code,
        (data) => {
          this.building = DataUtils.parseDataToSingleObject(data.data.building, Building);
          this.facility = DataUtils.parseDataToSingleObject(data.data.facility, Facility);
          this.technicalInfo = DataUtils.parseDataToSingleObject(data.data.technicalInfo, TechnicalInfo);
          this.state = DataUtils.parseDataToSingleObject(data.data.state, ElevatorState);
          this.facilityDocumentations = DataUtils.parseDataToObjectArray(data.data.facilityDocumentation, FacilityDocumentation);

          QRCodeService.readConnectableFacilities(this.facility.id,
              (res) => {
                this.restConnectableFacilities = DataUtils.parseDataToObjectArray(res.data, Facility);
              },
              (error) => {
                this.$logger.error("Could not load facilities, because: " + error);
                Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:facility_plural')}));
              }
          );
          // format savedOn dateTime of documentations
          for (let i = 0; i < this.facilityDocumentations.length; i++) {
            this.facilityDocumentations[i].formattedSavedOn = DateUtils.parseDateTimeForDisplay(this.facilityDocumentations[i].savedOn);
          }
          // translate and sort rule states
          EnumService.translateAndSortEnumObject(this.state.ruleStates, "enums:facility-documentation-types.")

          // define new field in state object, so we can save the required pictures in the indexedDB later
          this.state.requiredPictures = [];
          this.facilityPictures = data.data.requiredPictures;
          // read the picture enums from backend, translate and sort them
          this.facilityPictures = EnumService.translateAndSortEnum(this.facilityPictures, "enums:facility-documentation-types.")
          // push the facilityPicture objects to the previously created state requiredPicture array
          for (let i = 0; i < this.facilityPictures.length; i++) {
            this.state.requiredPictures.push(this.facilityPictures[i])
          }

          // set/update the according data in indexedDB equal to the data from backend
          indexedDBService.readFacilityState(this.state).then((res) => {
            if (!res) {
              this.fillRequiredDocumentations().then(() => {
                this.fillRequiredPictures().then(() => {
                  indexedDBService.saveFacilityState(this.state);
                  this.fillRuleAndPictureTable();
                })
              })
            } else {
              this.state = res;
              this.fillRuleAndPictureTable();
            }
          })

          if (this.technicalInfo.buildingPermit) {
            this.technicalInfo.buildingPermit = this.$refs.datePermit.parseDTO(this.technicalInfo.buildingPermit)
          }

          if (this.technicalInfo.permissionToUse) {
            this.technicalInfo.permissionToUse = this.$refs.datePermission.parseDTO(this.technicalInfo.permissionToUse)
          }

          if (this.technicalInfo.modernizedOn) {
            this.technicalInfo.modernizedOn = this.$refs.dateModernized.parseDTO(this.technicalInfo.modernizedOn);
          }

          if (this.technicalInfo.emergencyCallSystemGSM && !this.technicalInfo.emergencyType.includes('GSM_EMERGENCY_CALL_SYSTEM')) {
            this.technicalInfo.emergencyType.push('GSM_EMERGENCY_CALL_SYSTEM')
          }

          if (this.technicalInfo.emergencyAnalog && !this.technicalInfo.emergencyType.includes('ANALOG_SYSTEM')) {
            this.technicalInfo.emergencyType.push('ANALOG_SYSTEM')
          }

          if (this.technicalInfo.awmModule && !this.technicalInfo.emergencyType.includes('AWM_MODULE')) {
            this.technicalInfo.emergencyType.push('AWM_MODULE')
          }

          // set/update the according data in indexedDB equal to the data from backend
          indexedDBService.readTechnicalInfo(this.technicalInfo).then((res) => {
            // if the onboarding hasn't been saved before, there should be no saved indexedDB entry for this facility
            if (!res) {
              indexedDBService.saveTechnicalInfo(this.technicalInfo);
            } else {
              this.technicalInfo = res;
            }
          })

          // read elevator types
          this.getEnum(
              "elevator/type",
              "enums:technical.elevator-type.",
              this.restElevatorType,
              "elevator types",
              "elevator-type"
          )

          // read drive types
          this.getEnum(
              "elevator/drive",
              "enums:technical.drive.",
              this.restDrives,
              "elevator drive types",
              "drive"
          )

          // read engine room position types
          this.getEnum(
              "elevator/engine",
              "enums:technical.engine-room-position.",
              this.restEngineRoomPositions,
              "elevator engine types",
              "engine-room-position"
          )

          // read door position types
          this.getEnum(
              "elevator/door/position",
              "enums:technical.elevator-door-position.",
              this.restDoorPositions,
              "elevator door position types",
              "door-position"
          )

          // read door security types
          this.getEnum(
              "elevator/door/security",
              "enums:technical.elevator-door-security-types.",
              this.restDoorSecurityType,
              "elevator door security types",
              "security-type"
          )

          // read door types
          this.getEnum(
              "elevator/door/type",
              "enums:technical.elevator-door-types.",
              this.restDoorType,
              "elevator door types",
              "door-type"
          )

          // read emergency types
          this.getEnum(
              "elevator/emergency",
              "enums:technical.elevator-emergency-types.",
              this.restEmergencyType,
              "elevator emergency types",
              "emergency"
          )

          // read emergency types
          this.getEnum(
              "elevator/protection",
              "enums:technical.elevator-protection-types.",
              this.restProtectionType,
              "elevator protection types",
              "protection-type"
          )

          // hydraulic drive types
          this.getEnum(
              "elevator/drive/hydraulic",
              "enums:technical.elevator-hydraulic-drive-types.",
              this.restHydraulicDriveType,
              "elevator hydraulic drive types",
              "hydraulic"
          )

          // rope drive types
          this.getEnum(
              "elevator/drive/rope",
              "enums:technical.elevator-rope-drive-types.",
              this.restRopeDriveType,
              "elevator rope drive types",
              "rope"
          )

          // read current logged in User Profile
          UsersService.readProfileFromAPI(
              (resp) => {
                this.user = DataUtils.parseDataToSingleObject(resp.data, Users);

                // Get the customer, if the user is not an admin
                if (!this.isAdmin()) {
                  CustomerService.readFromAPI(this.user.customer,
                      (res) => {
                        this.customer = DataUtils.parseDataToSingleObject(res.data, Customer);
                        this.currentDate = DateUtils.getCurrentDateAsDEString();
                        this.isLoading = false;
                      }
                  )
                } else {
                  this.currentDate = DateUtils.getCurrentDateAsDEString();
                  this.isLoading = false;
                }
              },
              (error) => {
                Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:user')}))
                this.$logger.error(error);
              }
          )

          this.$refs.manufacturerTypeahead.initialise(this.technicalInfo.manufacturer);
          this.$refs.assemblerTypeahead.initialise(this.technicalInfo.assembler);
          this.translateAndGetElevatorStateFields();
        },
        (error) => {
          this.$logger.error("Failed loading data for code " + this.$route.params.code +", because: " + error);
          this.$router.push('/404');
        }
    )
  }
}
</script>
<style src="@/modules/book/views/public/QRCodePublic.scss" lang="scss"/>
